import { colour, type Screen, type LayoutCanvas, type LayoutDocument, type LayoutKind } from './types'

/** Background gradients, by name; internal/layout.Gradients names the same. */
export const GRADIENTS = ['dawn', 'mist', 'dusk'] as const
export type Gradient = (typeof GRADIENTS)[number]

/** A gradient preset as CSS: its token, or the fallback when a build lacks it. */
export function gradientPaint(name: string | undefined, fallback = 'var(--bg-page)'): string {
  const known = GRADIENTS.includes(name as Gradient) ? name : GRADIENTS[0]
  return `var(--canvas-gradient-${known}, ${fallback})`
}

/** The CSS that paints a canvas's ground: its colour, gradient or picture. */
export function canvasPaint(canvas: LayoutCanvas, assetURL?: (id: string) => string): string {
  const base = colour(canvas.background, 'var(--bg-page)')
  if (canvas.backgroundFill === 'gradient') return gradientPaint(canvas.backgroundGradient, base)
  const id = canvas.backgroundImage?.assetId
  if (canvas.backgroundFill === 'image' && id && assetURL) {
    return `center / cover no-repeat url("${assetURL(id)}"), ${base}`
  }
  return base
}

/** Whether a ground reads as light or dark; undefined where it follows the theme or is a picture. */
export function groundTone(canvas: LayoutCanvas): 'light' | 'dark' | undefined {
  if (canvas.backgroundFill === 'image') return undefined
  if (canvas.backgroundFill === 'gradient') return canvas.backgroundGradient === 'dusk' ? 'dark' : 'light'
  return hexTone(canvas.background)
}

/** Whether a #rgb or #rrggbb colour reads as light or dark; undefined for anything else. */
export function hexTone(value: string | undefined): 'light' | 'dark' | undefined {
  const hex = value?.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1]
  if (!hex) return undefined
  const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  // Above 0.179 black text out-contrasts white (WCAG relative luminance).
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! > 0.179 ? 'light' : 'dark'
}

/**
 * The ink tokens a canvas re-points so text stays readable on its ground: dark ink on a
 * light ground, light on a dark one. Only where the theme's own ink would not suit it.
 */
export function groundInk(canvas: LayoutCanvas): 'dark' | 'light' | undefined {
  const tone = groundTone(canvas)
  if (!tone) return undefined
  const themeInk = canvas.theme === 'dark' ? 'light' : canvas.theme ? 'dark' : undefined
  const ink = tone === 'light' ? 'dark' : 'light'
  return ink === themeInk ? undefined : ink
}

/**
 * The ink of an element that paints its own ground: a theme token follows the theme, a
 * picked colour gets the ink that reads on it. Without its own ground it takes the canvas's.
 */
export function elementInk(background: string | undefined, canvas: LayoutCanvas): 'dark' | 'light' | undefined {
  if (!background) return undefined
  if (background.startsWith('--')) return canvas.theme === 'dark' ? 'light' : canvas.theme ? 'dark' : undefined
  const tone = hexTone(background)
  return tone === 'light' ? 'dark' : tone === 'dark' ? 'light' : undefined
}

/** Whether the ambient light is drawn: a kiosk's welcome screen, or a thumbnail of one. */
export function ambientOn(doc: Pick<LayoutDocument, 'kind' | 'canvas'>, screen: Screen | undefined): boolean {
  return doc.kind === 'kiosk' && doc.canvas.ambient === true && (screen === undefined || screen === 'attract')
}

/** The design's brand logo; absent is none. */
export function logoSource(canvas: LayoutCanvas): 'none' | 'qmeter' | 'organisation' {
  if (canvas.logo?.source === 'organisation') return canvas.logo.assetId ? 'organisation' : 'none'
  return canvas.logo?.source === 'qmeter' ? 'qmeter' : 'none'
}

/** An element shown or hidden; undo records it like any edit. */
export function toggleHidden(doc: LayoutDocument, id: string): LayoutDocument {
  return {
    ...doc,
    elements: doc.elements.map((el) => {
      if (el.id !== id) return el
      if (!el.hidden) return { ...el, hidden: true }
      const { hidden: _gone, ...shown } = el
      return shown
    }),
  }
}

/** Which design-level settings a kind offers. Paper has none but its colour. */
export function lookOffered(kind: LayoutKind): { theme: boolean; fills: boolean; ambient: boolean } {
  const screen = kind !== 'ticket'
  return { theme: screen, fills: screen, ambient: kind === 'kiosk' }
}

/** "Portrait" or "Landscape", by the canvas's longer side. */
export function orientation(canvas: Pick<LayoutCanvas, 'width' | 'height'>): 'portrait' | 'landscape' {
  return canvas.height > canvas.width ? 'portrait' : 'landscape'
}
